import { useEffect, useState } from 'react'

/**
 * Returns a boolean that turns `true` only after `condition` has been continuously truthy for `delayMs`,
 * and turns `false` immediately when `condition` becomes falsy. Useful for "only show a loader if
 * something takes longer than X ms" patterns, where flashing a spinner for fast resolutions is undesirable.
 *
 * Unlike a debounce, the falsy → clear transition is immediate (no trailing delay).
 *
 * @example
 * const showWorkspaceLoader = useDelayedFlag(!isWorkspaceResolved, 1000)
 */
export function useDelayedFlag(condition: boolean, delayMs: number): boolean {
  const [flag, setFlag] = useState(false)

  useEffect(() => {
    if (!condition) {
      setFlag(false)
      return
    }
    const timeoutId = setTimeout(() => setFlag(true), delayMs)
    return () => clearTimeout(timeoutId)
  }, [condition, delayMs])

  return flag
}
